import { z } from 'zod';
import { oltFamily, type OltFamily } from './olt-family.js';
import type { OltVendor } from './olt.js';

/**
 * The picture on an OLT's page — what an operator uploaded, and the drawing
 * that stands in when nobody has.
 *
 * Two sources, one slot. An uploaded photograph of the actual chassis always
 * wins, because a photograph of THAT box in THAT rack is worth more than any
 * rendering of its model. When there is none — which is most OLTs, most of the
 * time — a bundled drawing of the family stands in, so the card is never blank
 * and the page never looks broken for the ordinary case.
 *
 * The drawings are approximations and are labelled as such wherever they are
 * shown. They are drawn from what this platform has actually read off the
 * fleet's chassis (see `basis` on each entry), not traced from vendor
 * photography, and they carry no vendor mark.
 */

// --- what an operator may upload ---------------------------------------------

/**
 * The image types accepted on upload, checked against the file's OWN bytes.
 *
 * SVG is absent deliberately, and it is the whole reason this list is a list
 * rather than a prefix test on `image/`. An SVG is a document: it can carry
 * `<script>`, event handlers and external references, and this file is served
 * back from the platform's own origin to a browser holding an operator's
 * session. Sanitising one well enough to serve inline is a moving target that
 * has to be won every time, whereas refusing costs nothing here — the bundled
 * artwork is ours, compiled into the app, and never travels through this path.
 * `branding.ts` excludes SVG from org logos for exactly the same reason.
 */
export const OLT_IMAGE_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
export type OltImageContentType = (typeof OLT_IMAGE_CONTENT_TYPES)[number];

/** What each accepted type is called on disk, for the served filename. */
export const OLT_IMAGE_EXTENSIONS: Record<OltImageContentType, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

/**
 * Four megabytes.
 *
 * Enough for a phone photograph of a rack without resizing it first, which is
 * how this picture will actually be taken, and small enough that the file
 * store, the backup that mirrors it and the operator on a phone tether are all
 * unbothered. A 40 MP original is a resize away, and the refusal says so.
 */
export const OLT_IMAGE_MAX_BYTES = 4 * 1024 * 1024;

/**
 * What the API says about a stored image. The storage key is deliberately NOT
 * here: where the bytes live is the server's business, and the only way to
 * read them is the OLT's own scoped route.
 */
export const oltImageSchema = z.object({
  contentType: z.enum(OLT_IMAGE_CONTENT_TYPES),
  size: z.number().int(),
  /** Doubles as the cache key — the URL carries it, so the browser re-fetches
   *  only when the picture actually changed and not on every 30s poll. */
  uploadedAt: z.number().int(),
});
export type OltImage = z.infer<typeof oltImageSchema>;

// --- the bundled artwork ------------------------------------------------------

/**
 * One drawing per family, described here and drawn in the web app.
 *
 * The description lives in shared so the two cannot disagree: the renderer is
 * a `Record<OltFamily, …>`, so adding a family to `OltFamily` fails the web
 * typecheck until it has a drawing, and `basis` is the sentence shown next to
 * the full image — an approximation that says what it is built from is honest,
 * one that says nothing is a claim.
 */
export interface ChassisArt {
  family: OltFamily;
  /** What the drawing is of, e.g. "ZTE ZXA10 C300". */
  label: string;
  /**
   * Card positions the drawing shows, or null where the count is not
   * established for the family. Never invented: a drawing with the wrong
   * number of slots teaches an operator something false about their rack.
   */
  slots: number | null;
  /** What the drawing was built from, shown beside the full image. */
  basis: string;
}

export const CHASSIS_ART: Record<OltFamily, ChassisArt> = {
  c3xx: {
    family: 'c3xx',
    label: 'ZTE ZXA10 C300',
    slots: 21,
    basis:
      'Drawn from what this fleet’s C300 reports about itself: one IEC_SHELF rack/shelf 1, cards enumerated from slot 0 to slot 20 (`show card-temperature`, `show card-power`), and a fan tray the chassis keys by name rather than by slot — so it is drawn as its own unit beside the shelf, not as a card in it.',
  },
  c6xx: {
    family: 'c6xx',
    label: 'ZTE ZXA10 C650',
    slots: null,
    basis:
      'Drawn from what this fleet’s C650 reports: a four-unit fan group (indices 0–3, `show fan`) and two power modules in slots 10 and 12 sharing a 6.3 kW DC budget (`show power`). The service card count is not established from any read this platform has taken, so the bays are shown without a number rather than guessed at.',
  },
  ma58xx: {
    family: 'ma58xx',
    label: 'Huawei MA5800-X17',
    slots: 8,
    basis:
      'Drawn from `display board` on this fleet’s X17: eight boards enumerated, a paired active/standby control (two H902MPLA, adjacent slots) and a power board, with a fan unit the chassis registers as an EMU in its own right (H902FMLA, reporting ~139 W).',
  },
  ma56xx: {
    family: 'ma56xx',
    label: 'Huawei MA5683T',
    slots: null,
    basis:
      'Drawn from `display board` on this fleet’s MA5683T: a shorter subrack than the X17, service boards low and the control board above them. Its fan area is drawn without an indicator because the chassis registers no environment unit at all — `Used or not : Not used` — and a lit LED would claim a reading nothing is taking.',
  },
  unknown: {
    family: 'unknown',
    label: 'Rack chassis',
    slots: null,
    basis:
      'A generic rack chassis. This OLT’s model string does not match a family this platform has drawings for, so nothing about the slot layout is implied — upload a photograph of the actual chassis and this is replaced.',
  },
};

/** The drawing that stands in for an OLT with no uploaded photograph. */
export function chassisArtFor(vendor: OltVendor, model: string | null): ChassisArt {
  return CHASSIS_ART[oltFamily(vendor, model)];
}
